import * as React from 'react'
import { useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Card } from '@factorialco/f0-react'
import {
  Carousel,
  OneAlert,
  OneEmptyState,
  Page,
  ResourceHeader,
  Spinner,
} from '@factorialco/f0-react/dist/experimental'
import { Info, Money, Upsell } from '@factorialco/f0-react/icons/app'
import { DistributorProductVariantsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import RequestDeviceModal from 'modules/it_management/components/RequestDeviceModal'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'

import emptyStateImage from './Empty-state-device-catalog-card.png'

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} € /month`
  }
  return `${costNumber.toFixed(2)} €`
}

const DeviceCatalogDetailPage: React.FC = () => {
  const { variantId } = useParams<{ variantId: string }>()
  const [showRequestModal, setShowRequestModal] = React.useState(false)

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const { data, loading, error } = useQuery(DistributorProductVariantsDocument, {
    variables: { ids: variantId ? [variantId] : [] },
    skip: !variantId,
  })

  const variant = data?.itManagement.distributorProductVariantsConnection?.nodes?.[0] || null

  if (!isItManagementEnabled) {
    return null
  }

  if (featureLoading || loading) {
    return (
      <Page>
        <Box flexDirection="row" alignItems="center" justifyContent="center" padding="s48">
          <Spinner size="large" />
        </Box>
      </Page>
    )
  }

  if (featureError || error || !variant) {
    return (
      <Page>
        <Box padding="s24">
          <OneEmptyState
            title={i18n.t('it_management.device_catalog.detail.not_found')}
            description={i18n.t('it_management.device_catalog.detail.not_found_description')}
            variant="critical"
          />
        </Box>
      </Page>
    )
  }

  const deviceName =
    variant.product?.brand && variant.product?.name
      ? `${variant.product.brand} ${variant.product.name}`
      : variant.sku || variant.id

  const description = variant.description || variant.product?.description || '-'
  const price = formatPrice(variant.firstOfferCost, variant.firstOfferPeriod)
  const deliveryInfo =
    variant.deliveryConditions &&
    variant.deliveryConditions.trim() !== '' &&
    variant.deliveryConditions !== '-'
      ? variant.deliveryConditions
      : i18n.t('it_management.device_catalog.standard_delivery')

  const productMediaImages: string[] =
    'productMediaImageUrls' in variant && Array.isArray(variant.productMediaImageUrls)
      ? variant.productMediaImageUrls
      : []
  const deviceImages =
    productMediaImages.length > 0
      ? productMediaImages
      : [variant.firstProductMediaImageUrl || emptyStateImage]
  const isInStock = variant.stock !== null && variant.stock !== undefined && variant.stock > 0

  return (
    <Page
      header={
        <ResourceHeader
          title={deviceName}
          description={description}
          status={{
            label: i18n.t('it_management.common.status'),
            text: i18n.t('it_management.assets.status.in_stock'),
            variant: 'positive',
          }}
          metadata={[
            {
              label: i18n.t('it_management.device_catalog.delivery_title'),
              value: {
                type: 'text',
                content: deliveryInfo,
              },
            },
          ]}
        />
      }
    >
      <Box flexDirection="row" gap="s24" padding="s24" style={{ alignItems: 'flex-start' }}>
        <Box gap="s24" flexDirection="column" style={{ flex: '1 1 auto', minWidth: '0' }}>
          <Box style={{ maxHeight: '300px', overflow: 'hidden' }}>
            <Carousel
              columns={{ default: 1 }}
              showArrows={deviceImages.length > 1}
              showDots={deviceImages.length > 1}
            >
              {deviceImages.map((imageUrl: string, idx: number) => (
                <img
                  key={idx}
                  src={imageUrl}
                  alt={`${deviceName} - ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                  }}
                />
              ))}
            </Carousel>
          </Box>

          <Box flexDirection="row" gap="s16" alignItems="center">
            {isInStock && (
              <Box
                flexDirection="row"
                gap="s4"
                alignItems="center"
                padding="s4"
                paddingX="s8"
                style={{
                  backgroundColor: 'var(--color-green50)',
                  borderRadius: '16px',
                  border: '1px solid var(--color-green200)',
                }}
              >
                <Box
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-green500)',
                  }}
                />
                <Text size="200" weight="medium" color="grey700">
                  {i18n.t('it_management.device_catalog.detail.in_stock')}
                </Text>
              </Box>
            )}
          </Box>

          <Box gap="s16" flexDirection="column" paddingTop="s24">
            <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s16">
              <Box
                flexGrow
                padding="s16"
                style={{
                  backgroundColor: 'var(--color-white)',
                  borderRadius: '8px',
                }}
              >
                <Text size="300" weight="bold" color="black">
                  {i18n.t('it_management.device_catalog.detail.management_title')}
                </Text>
                <Text size="200" weight="normal" color="grey700">
                  {i18n.t('it_management.device_catalog.detail.management_description')}
                </Text>
              </Box>
              <F0Button
                variant="outline"
                label={i18n.t('it_management.device_catalog.detail.more_info')}
                icon={Upsell}
              />
            </Box>

            <Box flexDirection="row" gap="s16" flexWrap="wrap">
              <Box style={{ flex: '0 1 calc(50% - 8px)', minWidth: '0' }}>
                <F0Card
                  avatar={{
                    type: 'emoji',
                    emoji: '😌',
                  }}
                  title={i18n.t('it_management.device_catalog.detail.features.zero_touch.title')}
                  description={i18n.t(
                    'it_management.device_catalog.detail.features.zero_touch.description'
                  )}
                />
              </Box>
              <Box style={{ flex: '0 1 calc(50% - 8px)', minWidth: '0' }}>
                <F0Card
                  avatar={{
                    type: 'emoji',
                    emoji: '🎯',
                  }}
                  title={i18n.t('it_management.device_catalog.detail.features.centralized.title')}
                  description={i18n.t(
                    'it_management.device_catalog.detail.features.centralized.description'
                  )}
                />
              </Box>
              <Box style={{ flex: '0 1 calc(50% - 8px)', minWidth: '0' }}>
                <F0Card
                  avatar={{
                    type: 'emoji',
                    emoji: '🔒',
                  }}
                  title={i18n.t('it_management.device_catalog.detail.features.security.title')}
                  description={i18n.t(
                    'it_management.device_catalog.detail.features.security.description'
                  )}
                />
              </Box>
              <Box style={{ flex: '0 1 calc(50% - 8px)', minWidth: '0' }}>
                <F0Card
                  avatar={{
                    type: 'emoji',
                    emoji: '💻',
                  }}
                  title={i18n.t('it_management.device_catalog.detail.features.lifecycle.title')}
                  description={i18n.t(
                    'it_management.device_catalog.detail.features.lifecycle.description'
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box style={{ width: '300px', flexShrink: '0' }}>
          <F0Card
            title={deviceName}
            description={description}
            primaryAction={{
              label: i18n.t('it_management.device_catalog.actions.request'),
              onClick: (e?: React.MouseEvent) => {
                e?.stopPropagation()
                setShowRequestModal(true)
              },
            }}
          >
            <Box gap="s16" flexDirection="column">
              {price !== '-' && (
                <Box gap="s4" flexDirection="row" alignItems="center">
                  <Money width={16} height={16} />
                  <Text size="200">{price}</Text>
                </Box>
              )}
            </Box>
          </F0Card>
          <Box
            style={{
              backgroundColor: 'var(--color-blue50)',
              border: 'none',
              borderRadius: '8px',
              marginTop: '16px',
            }}
          >
            <OneAlert
              variant="info"
              title={i18n.t('it_management.device_catalog.request_modal.info_title')}
              description={i18n.t('it_management.device_catalog.request_modal.info_description')}
              icon={Info}
            />
          </Box>
        </Box>
      </Box>

      {showRequestModal && variant && (
        <RequestDeviceModal variant={variant} onClose={() => setShowRequestModal(false)} />
      )}
    </Page>
  )
}

export default DeviceCatalogDetailPage
